<!-- Table Section -->
<div
  class="relative overflow-hidden"
  [attr.aria-busy]="observations$.loading$()"
>
  <!-- Mobile Card Layout -->
  <div class="space-y-3 pb-4 md:hidden">
    @for (element of pagedRecords$(); track element.dim_connection_id) {
      <div class="rounded-xs bg-white p-4 shadow-sm">
        <!-- Heat Score -->
        <div class="mb-3 flex items-center gap-2">
          <div
            class="h-2 w-14 overflow-hidden rounded-full bg-white ring-1 ring-gray-200"
            aria-hidden="true"
          >
            <span
              [class]="
                'block h-full rounded-full transition-all duration-120 ease-in-out ' +
                getHeatIndicatorClass(element)
              "
              [style.width.%]="getHeatBarWidth(element)"
            ></span>
          </div>
          <span class="text-sm font-semibold tabular-nums">
            {{ formatHeatScore(element) }}
          </span>
        </div>

        <!-- Location and Link -->
        <div class="mb-3 flex gap-2">
          <div class="flex-1">
            <span class="text-xs font-semibold uppercase">Location</span>
            <p class="text-sm font-medium">
              {{ element.name_customer_location }}
            </p>
          </div>
          <div class="flex-1">
            <span class="text-xs font-semibold uppercase">Link</span>
            <p class="text-sm font-medium break-all">
              {{ element.dim_connection_id }}
            </p>
          </div>
        </div>

        <!-- Utilization Badges -->
        <div class="mb-3 flex gap-2">
          <div class="flex-1">
            <span class="mb-1 block text-xs font-semibold uppercase">In %</span>
            <span
              [class]="
                'inline-flex w-full items-center justify-center rounded-full px-2.5 py-0.5 text-sm font-semibold tabular-nums transition-colors duration-120 ease-in-out ' +
                getHeatLevelClass(
                  element.p95_utilization_rx_pct,
                  maxUtilization$().rx
                )
              "
            >
              {{ formatPercentage(element.p95_utilization_rx_pct) }}
            </span>
          </div>
          <div class="flex-1">
            <span class="mb-1 block text-xs font-semibold uppercase"
              >Out %</span
            >
            <span
              [class]="
                'inline-flex w-full items-center justify-center rounded-full px-2.5 py-0.5 text-sm font-semibold tabular-nums transition-colors duration-120 ease-in-out ' +
                getHeatLevelClass(
                  element.p95_utilization_tx_pct,
                  maxUtilization$().tx
                )
              "
            >
              {{ formatPercentage(element.p95_utilization_tx_pct) }}
            </span>
          </div>
        </div>

        <!-- Speeds -->
        <div class="flex gap-2">
          <div class="flex-1">
            <span class="mb-1 block text-xs font-semibold uppercase"
              >Speed In</span
            >
            <p class="text-sm font-medium">
              {{ formatSpeed(element.provisioned_speed_rx_mbps) }} Mbps
            </p>
          </div>
          <div class="flex-1">
            <span class="mb-1 block text-xs font-semibold uppercase"
              >Speed Out</span
            >
            <p class="text-sm font-medium">
              {{ formatSpeed(element.provisioned_speed_tx_mbps) }} Mbps
            </p>
          </div>
        </div>
      </div>
    }
  </div>

  <!-- Loading Skeleton for Mobile -->
  @if (observations$.loading$()) {
    <div class="space-y-3 pb-4 md:hidden">
      @for (i of [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]; track i) {
        <div class="rounded-sm bg-white p-4 shadow-sm">
          <div class="mb-3 h-2 w-14 rounded-full bg-gray-300"></div>
          <div class="mb-2 h-4 w-32 rounded bg-gray-300"></div>
          <div class="mb-3 h-4 w-40 rounded bg-gray-300"></div>
          <div class="mb-3 flex gap-2">
            <div class="h-8 flex-1 rounded-full bg-gray-300"></div>
            <div class="h-8 flex-1 rounded-full bg-gray-300"></div>
          </div>
          <div class="flex gap-2">
            <div class="h-4 flex-1 rounded bg-gray-300"></div>
            <div class="h-4 flex-1 rounded bg-gray-300"></div>
          </div>
        </div>
      }
    </div>
  }

  <!-- Table (hidden on mobile/tablet, shown on lg+) -->
  <div class="hidden md:block">
    <table
      mat-table
      [dataSource]="pagedRecords$()"
      matSort
      matSortDisableClear
      matSortActive="heat"
      matSortDirection="desc"
      (matSortChange)="onMatSortChange($event)"
      class="w-full text-sm"
      aria-label="P95 utilization data by site"
    >
      <!-- Utilization Score Column (sorted by weighted max/min utilization score) -->
      <ng-container matColumnDef="heat">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header="heat"
          scope="col"
        >
          <span class="inline-flex items-center gap-2">
            <mat-icon
              class="block bg-[linear-gradient(to_right,#0ea5e9_0%,#a855f7_50%,#dc2626_100%)] bg-clip-text text-xl! text-transparent"
              aria-hidden="true"
              >whatshot</mat-icon
            >
            <span class="font-bold uppercase">Utilization</span>
          </span>
          <span class="sr-only"
            >Sorted by utilization score: maximum of incoming and outgoing
            percentage plus half of the minimum percentage.</span
          >
        </th>
        <td mat-cell *matCellDef="let element">
          <div class="flex items-center gap-2">
            <div
              class="h-2 w-14 overflow-hidden rounded-full bg-white ring-1 ring-gray-200"
              aria-hidden="true"
            >
              <span
                [class]="
                  'block h-full rounded-full transition-all duration-120 ease-in-out ' +
                  getHeatIndicatorClass(element)
                "
                [style.width.%]="getHeatBarWidth(element)"
              ></span>
            </div>
            <span
              class="inline-flex min-w-8 text-xs font-semibold tabular-nums"
            >
              {{ formatHeatScore(element) }}
            </span>
          </div>
        </td>
      </ng-container>

      <!-- Customer Location Column -->
      <ng-container matColumnDef="name_customer_location">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header="name_customer_location"
          scope="col"
        >
          <span class="py-4 font-bold uppercase">Location</span>
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.name_customer_location }}
        </td>
      </ng-container>

      <!-- Connection ID Column -->
      <ng-container matColumnDef="dim_connection_id">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header="dim_connection_id"
          scope="col"
        >
          <span class="font-bold uppercase">Link</span>
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.dim_connection_id }}
        </td>
      </ng-container>

      <!-- P95 RX Utilization Column -->
      <ng-container matColumnDef="p95_utilization_rx_pct">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header="p95_utilization_rx_pct"
          class="text-right"
          scope="col"
        >
          <span class="font-bold uppercase">In %</span>
        </th>
        <td mat-cell *matCellDef="let element" class="text-right">
          <span
            [class]="
              'inline-flex min-w-19 items-center justify-end rounded-full px-2.5 py-0.5 font-semibold tabular-nums transition-colors duration-120 ease-in-out ' +
              getHeatLevelClass(
                element.p95_utilization_rx_pct,
                maxUtilization$().rx
              )
            "
          >
            {{ formatPercentage(element.p95_utilization_rx_pct) }}
          </span>
        </td>
      </ng-container>

      <!-- P95 TX Utilization Column -->
      <ng-container matColumnDef="p95_utilization_tx_pct">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header="p95_utilization_tx_pct"
          class="text-right"
          scope="col"
        >
          <span class="font-bold uppercase">Out %</span>
        </th>
        <td mat-cell *matCellDef="let element" class="text-right">
          <span
            [class]="
              'inline-flex min-w-19 items-center justify-end rounded-full px-2.5 py-0.5 font-semibold tabular-nums transition-colors duration-120 ease-in-out ' +
              getHeatLevelClass(
                element.p95_utilization_tx_pct,
                maxUtilization$().tx
              )
            "
          >
            {{ formatPercentage(element.p95_utilization_tx_pct) }}
          </span>
        </td>
      </ng-container>

      <!-- Provisioned RX Speed Column -->
      <ng-container matColumnDef="provisioned_speed_rx_mbps">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header="provisioned_speed_rx_mbps"
          scope="col"
        >
          <span class="font-bold uppercase">Speed In</span>
        </th>
        <td mat-cell *matCellDef="let element" class="text-right">
          {{ formatSpeed(element.provisioned_speed_rx_mbps) }}
        </td>
      </ng-container>

      <!-- Provisioned TX Speed Column -->
      <ng-container matColumnDef="provisioned_speed_tx_mbps">
        <th
          mat-header-cell
          *matHeaderCellDef
          mat-sort-header="provisioned_speed_tx_mbps"
          scope="col"
        >
          <span class="font-bold uppercase">Speed Out</span>
        </th>
        <td mat-cell *matCellDef="let element" class="text-right">
          {{ formatSpeed(element.provisioned_speed_tx_mbps) }}
        </td>
      </ng-container>

      <tr
        mat-header-row
        *matHeaderRowDef="columns"
        class="hidden md:table-row"
      ></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: columns"
        class="border-b border-gray-100 bg-white transition-colors"
      ></tr>
    </table>
  </div>

  <!-- Loading Skeleton (Desktop) -->
  @if (observations$.loading$()) {
    <table
      class="hidden w-full text-sm md:table"
      role="status"
      aria-live="polite"
      aria-label="Loading utilization records"
    >
      <tbody>
        @for (i of [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]; track i) {
          <tr
            class="border-b border-gray-100 bg-white"
            [class.bg-gray-50]="i % 2 === 1"
          >
            <!-- Heat bar skeleton -->
            <td class="w-20 px-4 py-4">
              <div class="h-2 w-14 rounded-full bg-gray-300"></div>
            </td>
            <!-- Location skeleton -->
            <td class="px-4 py-4">
              <div class="h-4 w-40 rounded bg-gray-300"></div>
            </td>
            <!-- Link skeleton -->
            <td class="px-4 py-4">
              <div class="h-4 w-32 rounded bg-gray-300"></div>
            </td>
            <!-- In % skeleton -->
            <td class="px-4 py-4 text-right">
              <div class="ml-auto h-6 w-20 rounded-full bg-gray-300"></div>
            </td>
            <!-- Out % skeleton -->
            <td class="px-4 py-4 text-right">
              <div class="ml-auto h-6 w-20 rounded-full bg-gray-300"></div>
            </td>
            <!-- Speed In skeleton -->
            <td class="px-4 py-4 text-right">
              <div class="ml-auto h-4 w-24 rounded bg-gray-300"></div>
            </td>
            <!-- Speed Out skeleton -->
            <td class="px-4 py-4 text-right">
              <div class="ml-auto h-4 w-24 rounded bg-gray-300"></div>
            </td>
          </tr>
        }
      </tbody>
    </table>
  }

  @if (!observations$.loading$() && filteredRecords$().length === 0) {
    <div class="flex flex-col items-center justify-center p-16 text-center">
      <mat-icon
        class="mb-4 h-16! w-16! text-6xl! text-gray-300"
        aria-hidden="true"
        >storage</mat-icon
      >
      <p class="text-base font-medium text-gray-700">
        No utilization records found
      </p>
      <p class="mt-1 text-sm text-gray-500">
        Try adjusting your filters or search criteria
      </p>
    </div>
  }
</div>
